import { useContext, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import { createId } from '@paralleldrive/cuid2';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { ChatWelcomeScreenContext } from '@trinity/chat-components/containers/BrandInsights/Context';
import { User } from '@trinity/chat-components/components/icons/User';
import { AuthorContext } from './context';

type Props = {
  onClick: () => void;
};

export const KolWelcome = (props: Props) => {
  /*
  const dispatch = useContext(ChatStateDispatchContext);

  const handleNewConversation = useCallback(() => {
    dispatch({
      type: 'kol-search-added',
      payload: {
        item: {
          id: null,
          createdOn: new Date(),
          status: KolSearchStatus.QuestionsBank,
          identifier: createId(),
          followupQuestions: [],
          form: {
            name: '',
          },
          results: null,
          profile: null,
          recommendations: {
            status: 'skipped',
            items: [],
            total: 0,
          },
          citations: [],
        },
      },
    });
  }, [dispatch]);
  */

  const author = useContext(AuthorContext);
  const { content } = useContext(ChatWelcomeScreenContext);

  return (
    <CardRoot>
      {author.profile.photoUrl
        ? <UserAvatar url={author.profile.photoUrl} />
        : <UserIcon />}
      <NameText>
        {author.profile.name}
      </NameText>
      <Box sx={{ maxWidth: 770, padding: '20px 0' }}>
        {content ? (
          <MarkdownContent>
            <BasicMarkdown>{content}</BasicMarkdown>
          </MarkdownContent>
        ) : (
          <>
            <ContentTitle>{copy.title}</ContentTitle>
            <ContentText>{copy.message}</ContentText>
          </>
        )}
        <Footer>
          <NewConversationButton onClick={props.onClick}>
            <AddIconStyled />
            <NewConversationText>
              New Conversation
            </NewConversationText>
          </NewConversationButton>
        </Footer>
      </Box>
    </CardRoot>
  );
};

KolWelcome.displayName = 'Kol.Welcome';

const CardRoot = styled.div(({ theme }) => ({
  padding: 15,
  borderRadius: 8,
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light4}`,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '900px',
  margin: '30px auto',
}));

const UserAvatar = styled.div<{ url: string }>`
  width: 100px;
  height: 100px;
  background-image: ${({ url }) => `url(${url})`};
  background-size: cover;
  border-radius: 50%;
`;

const UserIcon = styled(User)`
  color: var(--black-l);
  width: 100px;
  height: 100px;
  font-family: ${({ theme }) => theme.fonts.regular};
`;

const NameText = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 22,
  color: theme.palette.primary.main,
  marginTop: 10,
  textAlign: 'center',
  marginBottom: 10,
}));

const MarkdownContent = styled.div({
  textAlign: 'center',
  marginBottom: 15,
});

const ContentTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 22,
  color: theme.palette.black.main,
  textAlign: 'center',
  marginBottom: 15,
}));

const ContentText = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 16,
  color: theme.palette.black.main,
  lineHeight: '22px',
  textAlign: 'center',
  marginBottom: 15,
}));

const copy = {
  message: `Your call planning assistant! Plan your calls in advance with key questions, obtain HCP insights, and prepare well for better engagement and outcomes.`,
  title: `Welcome to Pre-Call Planner!`,
};

const NewConversationButton = styled.button(({ theme }) => ({
  backgroundColor: theme.palette.sentiment.primary.main,
  padding: '10px 15px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  color: theme.palette.secondary.main,
  fontSize: '13px',
  borderRadius: '50px',
  textTransform: 'uppercase',
  transition: 'background-color 0.3s',

  '&:hover': {
    backgroundColor: theme.palette.sentiment.primary.dark,
  },
}));

const NewConversationText = styled.div`
  color: ${props => props.theme.palette.secondary.main};
  font-family: ${props => props.theme.fonts.semiBold};
`;

const AddIconStyled = styled(AddIcon)(({ theme }) => ({
  color: theme.palette.secondary.main,
  width: 18,
  height: 18,
}));

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 0;
`;
